import styles from "./Zoomable.module.less";
import {
  MouseEvent,
  ReactNode,
  useCallback,
  useDeferredValue,
  useEffect,
  useState,
} from "react";

interface Position {
  x: number;
  y: number;
}

interface Props {
  children: ReactNode;
  zoomStep?: number;
  maxZoom?: number;
  minZoom?: number;
}

interface ZoomOffsetParams {
  zoom: number;
  newZoom: number;
  offset: Position;
  target: HTMLDivElement;
  clientX: number;
  clientY: number;
}

const getZoomOffset = ({
  zoom,
  newZoom,
  offset,
  target,
  clientX,
  clientY,
}: ZoomOffsetParams) => {
  const wrapperCenterX = target.clientWidth / 2;
  const wrapperCenterY = target.clientHeight / 2;

  const rect = target.getBoundingClientRect();
  const mouseX = clientX - rect.x;
  const mouseY = clientY - rect.y;

  const wrapperMouseOffsetX = mouseX - wrapperCenterX;
  const wrapperMouseOffsetY = mouseY - wrapperCenterY;

  const mouseOffsetX = wrapperMouseOffsetX / zoom - offset.x;
  const mouseOffsetY = wrapperMouseOffsetY / zoom - offset.y;

  const newOffsetX = wrapperMouseOffsetX / newZoom;
  const newOffsetY = wrapperMouseOffsetY / newZoom;

  return {
    x: newOffsetX - mouseOffsetX,
    y: newOffsetY - mouseOffsetY,
  };
};

export const Zoomable = ({
  children,
  zoomStep = 0.5,
  maxZoom = 3,
  minZoom = 1,
}: Props) => {
  const [zoom, setZoom] = useState<number>(1);

  const [startPosition, setStartPosition] = useState<Position | null>(null);
  const [currentPosition, setCurrentPosition] = useState<Position | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // using it to skip some frames when dragging
  const currentPositionDeferred = useDeferredValue(currentPosition);

  const zoomIn = useCallback(() => {
    const newZoom = zoom + zoomStep;
    if (newZoom > maxZoom) return zoom;
    setZoom(newZoom);
    return newZoom;
  }, [maxZoom, zoomStep, zoom]);

  const zoomOut = useCallback(() => {
    const newZoom = zoom - zoomStep;
    if (newZoom < minZoom) return zoom;
    setZoom(newZoom);
    return newZoom;
  }, [minZoom, zoomStep, zoom]);

  const setCenter = useCallback(() => {
    setZoom(1);

    setStartPosition(null);
    setCurrentPosition(null);
    setOffset({ x: 0, y: 0 });
  }, []);

  const startMove = useCallback(
    (x: number, y: number) => {
      const startX = x / zoom - offset.x;
      const startY = y / zoom - offset.y;

      setStartPosition({ x: startX, y: startY });
      setCurrentPosition({ x, y });
      setOffset({
        x: x / zoom - startX,
        y: y / zoom - startY,
      });
    },
    [zoom, offset]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => startMove(e.pageX, e.pageY),
    [startMove]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      startMove(e.touches[0].pageX, e.touches[0].pageY);
    },
    [startMove]
  );

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    setCurrentPosition({ x: e.touches[0].pageX, y: e.touches[0].pageY });
  }, []);

  const handleMoveEnd = useCallback(() => setStartPosition(null), []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => setCurrentPosition({ x: e.pageX, y: e.pageY }),
    []
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (e.deltaY === 0) return;
      const newZoom = e.deltaY < 0 ? zoomIn() : zoomOut();
      setOffset(
        getZoomOffset({
          zoom,
          newZoom,
          offset,
          target: e.currentTarget,
          clientX: e.clientX,
          clientY: e.clientY,
        })
      );
    },
    [zoom, zoomIn, zoomOut, offset]
  );

  useEffect(() => {
    if (!startPosition || !currentPositionDeferred) return;

    setOffset({
      x: currentPositionDeferred.x / zoom - startPosition.x,
      y: currentPositionDeferred.y / zoom - startPosition.y,
    });
  }, [zoom, startPosition, currentPositionDeferred]);

  return (
    <div
      className={styles.container}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleMoveEnd}
      onTouchMove={handleTouchMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMoveEnd}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMoveEnd}
      onWheel={handleWheel}
    >
      <div className={styles.toolbar}>
        <button
          className={styles.btn}
          type="button"
          onClick={zoomIn}
          disabled={zoom === maxZoom}
        >
          +
        </button>
        <button
          className={styles.btn}
          type="button"
          onClick={zoomOut}
          disabled={zoom === minZoom}
        >
          -
        </button>
        <button className={styles.btn} type="button" onClick={setCenter}>
          0
        </button>
      </div>
      <div
        className={styles.area}
        style={{
          transform: `scale(${zoom}) translate(${offset.x}px, ${offset.y}px)`,
        }}
      >
        {children}
      </div>
    </div>
  );
};
